// React
import React, { useState, useEffect, useMemo } from "react";

// Libraries
import PropTypes from "prop-types";
import { Button, Icon, Menu, Popup, Divider, Checkbox } from "semantic-ui-react";
import { get, isEmpty, some, find } from "lodash";
import moment from "moment";

// Api
import Api from "@Api";

// Utils
import { formatPhone } from "@Utils/formats";

// Redux
import { connect } from "react-redux";
import { setSearchMessages, toggleViewAutogestion, freeChat, closeChat, sendMessage } from "@Actions/chats";
import { displayNotification } from "@Actions/notifications";

// Components
import Text from "@Components/Text";
import PlatformIcon from "@Components/PlatformIcon";

// Hooks
import usePanelVisibility from "@Hooks/usePanelVisibility";

// Context
import { useListRef } from "@Features/Monitor/Context";

// Utils
import { messageBuilder } from "@Utils/functionality";

// Modals
import QuickAnswers from "@Features/Monitor/Modals/QuickAnswers";
import Transfer from "@Features/Monitor/Modals/Transfer";
import Shedule from "@Features/Monitor/Modals/Shedule";
import Tags from "@Features/Monitor/Modals/Tags";
import Hashtag from "@Features/Monitor/Modals/Hashtag";
import ConfirmModal from "@Modals/ConfirmModal";
import PlantillaModal from "@Features/Monitor/Modals/PlantillaModal";
import FlowSelectModal from "@Features/Monitor/Modals/FlowSendModal/FlowSelectModal";
import FlowSendModal from "@Features/Monitor/Modals/FlowSendModal";
import NotaModal from "@Features/Monitor/Modals/NotaModal";
import NotePreview from "@Features/Monitor/Modals/NotePreview";

// Styles
import styles from "./styles.module.css";

const ChatHeader = ({
	dispatch,
	isDisabled,
	currentChat,
	handlePrint,
	isHistoric,
	permissions,
	settings,
	command,
	setCommand,
	cmdSearch,
	setCmdSearch,
	isViewAutogestion,
	isWhatsappBlock,
	onSuccessSend,
	userName,
	blackListKeyWords,
}) => {
	const { goList, goInfo, isMobile, isDesktop } = usePanelVisibility();
	const { listRef } = useListRef();
	const [openQuickAnswers, setOpenQuickAnswers] = useState(false);
	const [openPlantillaModal, setOpenPlantillaModal] = useState(false);
	const [openHashtag, setOpenHashtag] = useState(false);
	const [openSchedule, setOpenSchedule] = useState(false);
	const [openTags, setOpenTags] = useState(false);
	const [openTransfer, setOpenTransfer] = useState(false);
	const [openConfirm, setOpenConfirm] = useState(false);
	const [confirmText, setConfirmText] = useState(false);
	const [onTagClose, setOnTagClose] = useState(false);
	const [enviarFlow, setEnviarFlow] = useState(false);
	const [selectedFlowForSend, setSelectedFlowForSend] = useState(null);
	const [withMsgClose, setWithMsgClose] = useState(true);
	const [popupOpen, setPopupOpen] = useState(false);
	const [openNotaModal, setOpenNotaModal] = useState(false);
	const [openNotePreview, setOpenNotePreview] = useState(false);
	const [notaTag, setNotaTag] = useState(null);
	const [initialNotas, setInitialNotas] = useState({});
	const [notasMap, setNotasMap] = useState({});
	const tagOnClose = get(settings, "tagOnClose", false);
	const endChatNoResponse = get(settings, "endChatNoResponse", true);
	const isEmail = get(currentChat, "channel.platform.name", "") === "Email";
	const isTemplateBlock = useMemo(() => {
		const templateMsg = find(currentChat.messages, { forTemplate: true });
		if (templateMsg) {
			return moment().diff(moment(templateMsg.createdDate), "hours") < 1;
		}
		return false;
	}, [popupOpen]);

	const noSendTemplate = permissions?.noSendTemplate === true || permissions?.noSendTemplate === "true";

	useEffect(() => {
		if (!isEmpty(command)) {
			switch (String(command).toLowerCase()) {
				case "r": {
					if (!isWhatsappBlock) setOpenQuickAnswers(true);
					break;
				}
				case "e": {
					setOpenTags(true);
					break;
				}
				case "t": {
					setOpenTransfer(true);
					break;
				}
				case "l": {
					setConfirmText("FreeChat");
					setOpenConfirm(true);
					break;
				}
				case "f": {
					setConfirmText("CloseChat");
					setOpenConfirm(true);
					break;
				}
				case "p": {
					if (isWhatsappBlock && !isTemplateBlock && !isDisabled && !noSendTemplate) setOpenPlantillaModal(true);
					break;
				}
				case "n": {
					if (!isHistoric && !isDisabled) setOpenNotePreview(true);
					break;
				}
				default:
					break;
			}
		}
	}, [command]);

	useEffect(() => {
		const fetchNotas = async () => {
			const chatId = get(currentChat, "id", null);
			if (!chatId) return;
			try {
				const res = await Api.get("chattags", { params: { chatId } });
				const chatTags = get(res, "data.data", []);
				const map = {};
				chatTags.forEach((ct) => {
					if (ct.nota) {
						map[ct.tagId] = ct.nota;
					}
				});
				setNotasMap(map);
			} catch (error) {
				setNotasMap({});
			}
		};
		fetchNotas();
	}, [get(currentChat, "id", null)]);

	const handleSearch = () => {
		dispatch(setSearchMessages(get(currentChat, "id", null)));
		if (!isDesktop) {
			goInfo();
		}
	};

	const handleViewAutogestion = () => {
		dispatch(toggleViewAutogestion());
	};

	const sheduleSave = async (dataShedule) => {
		const sheduleModalVal = {
			name: currentChat.contact.name,
			phone: currentChat.contact.phone,
			datetime: dataShedule.scheduleDate,
		};

		await dispatch("scheduleChat"(dataShedule));
		"setModalConfirmShedule"(sheduleModalVal);
	};

	const openPopup = () => {
		setPopupOpen(true);
	};

	const closePopup = () => {
		setPopupOpen(false);
	};

	const handleTagClickNota = async (tag) => {
		try {
			const res = await Api.get("chattags", { params: { chatId: currentChat.id } });
			const chatTags = get(res, "data.data", []);
			const tagNota = find(chatTags, (ct) => {
				return ct.tagId === tag.id;
			});
			const notaValue = get(tagNota, "nota", "");
			setInitialNotas({ [tag.id]: notaValue });
			setNotaTag(tag);
			setOpenNotaModal(true);
		} catch (error) {
			setInitialNotas({});
			setNotaTag(tag);
			setOpenNotaModal(true);
		}
	};

	const handleNotaConfirm = async (result) => {
		const item = result[0];
		if (item) {
			await Api.post("chattags", {
				chatId: currentChat.id,
				tagId: item.tagId,
				nota: item.nota,
			});
			setNotasMap((prev) => {
				return { ...prev, [item.tagId]: item.nota };
			});
			dispatch(displayNotification({ text: "Nota guardada correctamente" }));
		}
		setOpenNotaModal(false);
		setNotaTag(null);
		setInitialNotas({});
	};

	const handleNotaClose = () => {
		setOpenNotaModal(false);
		setNotaTag(null);
		setInitialNotas({});
	};

	const closeNotePreview = () => {
		setCommand("");
		setCmdSearch("");
		setOpenNotePreview(false);
	};

	const handleSendNote = async ({ file, caption }) => {
		let result = false;
		if (file) {
			const mimeType = file.type;
			const tmp = file.name.split(".");
			const extension = tmp[tmp.length - 1];
			const url = URL.createObjectURL(file);
			const newFile = new Blob([file], { type: mimeType });
			newFile.lastModifiedDate = new Date();
			newFile.name = `Note_${Date.now()}.${extension}`;
			const newMessage = messageBuilder({
				chat: currentChat,
				type: "Note",
				reply: {},
				userName,
				text: caption || "",
				uri: url,
			});
			result = await dispatch(sendMessage({ message: newMessage, file: newFile, listRef }));
		} else {
			const newMessage = messageBuilder({
				chat: currentChat,
				type: "Note",
				reply: {},
				userName,
				text: caption || "",
				uri: null,
			});
			result = await dispatch(sendMessage({ message: newMessage, listRef }));
		}
		if (result && onSuccessSend) {
			onSuccessSend();
		}
		return result;
	};

	const dismissConfirm = () => {
		setCommand("");
		setCmdSearch("");
		setOpenConfirm(false);
	};

	const confirmHandler = (withMsg = true) => {
		const userEndChatNoResponse = get(permissions, "endChatNoResponse", false);
		// console.log(userEndChatNoResponse, "userEndChatNoResponse");

		if (confirmText === "FreeChat") {
			dispatch(freeChat(currentChat.id));
		} else if (
			!endChatNoResponse &&
			!userEndChatNoResponse &&
			!some(currentChat.messages, { sentByAgent: true, chatId: currentChat.id, autogestion: null })
		) {
			dispatch(displayNotification({ text: "Debe atender este chat para poder finalizar", isError: true }));
		} else if (tagOnClose && isEmpty(currentChat.tags)) {
			setOnTagClose(true);
			setWithMsgClose(withMsg);
			setOpenTags(true);
		} else {
			dispatch(closeChat(currentChat.id, currentChat.channelCid, withMsg));
		}
		setOpenConfirm(false);
	};

	const onSuccesTags = () => {
		if (onTagClose) {
			setOnTagClose(false);
			dispatch(closeChat(currentChat.id, currentChat.channelCid, withMsgClose));
		}
	};

	const closeTransfer = () => {
		setCommand("");
		setCmdSearch("");
		setOpenTransfer(false);
	};

	const closeQuickAnswers = () => {
		setCommand("");
		setCmdSearch("");
		setOpenQuickAnswers(false);
	};

	const closePlantilla = () => {
		setCommand("");
		setCmdSearch("");
		setOpenPlantillaModal(false);
	};

	const closeTags = () => {
		setCommand("");
		setCmdSearch("");
		setOpenTags(false);
		setOnTagClose(false);
	};

	return (
		<div className={styles.container}>
			{/* Modales */}
			{openTransfer && <Transfer search={cmdSearch} chatCurrent={currentChat} onClose={closeTransfer} buttonLabel="Transferir" />}
			{openNotaModal && notaTag && (
				<NotaModal tags={[notaTag]} initialNotas={initialNotas} onClose={handleNotaClose} onConfirm={handleNotaConfirm} />
			)}
			{openNotePreview && (
				<NotePreview text={cmdSearch} handleDismiss={closeNotePreview} handleSend={handleSendNote} blackListKeyWords={blackListKeyWords} />
			)}
			{openSchedule && (
				<Shedule
					chatCurrent={currentChat}
					onSendData={() => {
						sheduleSave();
					}}
					groups={[]}
					onClose={() => {
						setOpenSchedule(false);
					}}
				/>
			)}
			{openHashtag && (
				<Hashtag
					onClose={() => {
						setOpenHashtag(false);
					}}
				/>
			)}
			{openQuickAnswers && (
				<QuickAnswers search={cmdSearch} currentChat={currentChat} onClose={closeQuickAnswers} onSuccessSend={onSuccessSend} />
			)}
			{openPlantillaModal && (
				<PlantillaModal
					search={cmdSearch}
					currentChat={currentChat}
					onClose={closePlantilla}
					channel={currentChat.channel}
					contact={currentChat.contact}
					isReadOnly
					modalCrud="Add"
				/>
			)}
			{openTags && (
				<Tags
					search={cmdSearch}
					currentChat={currentChat}
					onClose={closeTags}
					onSuccesTags={onSuccesTags}
					onTagClose={onTagClose}
					labelTip="Etiquetar"
				/>
			)}
			{openConfirm && (
				<ConfirmModal
					buttonLabel={confirmText === "FreeChat" ? "Liberar" : "Finalizar"}
					icon={confirmText === "FreeChat" ? "sync" : "close"}
					visible
					focus={currentChat.contact.name || currentChat.contact.phone}
					header="Confirmar"
					content={confirmText === "FreeChat" ? "Está seguro que desea liberar el chat" : "Está seguro que desea finalizar el chat"}
					onConfirm={() => {
						return confirmHandler();
					}}
					onDismiss={dismissConfirm}
					extraButton={
						confirmText === "CloseChat" && permissions.allowEndChatWithoutMessage === true ? (
							<Button
								className={styles.buttonMsj}
								onClick={() => {
									return confirmHandler(false);
								}}>
								Finalizar sin msj
							</Button>
						) : null
					}
				/>
			)}
			{enviarFlow && (
				<FlowSelectModal
					open={enviarFlow}
					onClose={() => {
						setEnviarFlow(false);
					}}
					onSend={(flow) => {
						setEnviarFlow(false);
						setSelectedFlowForSend({ ...flow, directSend: true });
					}}
					onConfigure={(flow) => {
						setEnviarFlow(false);
						setSelectedFlowForSend(flow);
					}}
					allowSendFlow={permissions.allowSendFlow}
					allowConfigFlow={permissions.allowConfigFlow}
				/>
			)}
			{selectedFlowForSend && (
				<FlowSendModal
					open={!!selectedFlowForSend}
					flow={selectedFlowForSend}
					currentChat={currentChat}
					onClose={() => {
						setSelectedFlowForSend(null);
					}}
					onSuccessSend={onSuccessSend}
				/>
			)}
			{isMobile && (
				<div className={styles.backIcon} tabIndex={-1} role="button" onClick={goList}>
					<Icon link name="chevron left" />
				</div>
			)}
			<div className={`${styles.contactContainer} ${!isDesktop ? styles.touch : ""}`} tabIndex={-1} role="button" onClick={goInfo}>
				<PlatformIcon name={get(currentChat, "channel.platform.name", "")} />
				<div className={styles.contactInfo}>
					<Text className={styles.contactName}>{`${get(currentChat, "contact.name", "")}${
						isEmail ? ` - ${get(currentChat, "id", "")}` : ""
					}`}</Text>
					<Text className={styles.contactPhone}>
						{isEmail ? get(currentChat, "contact.email", "") : formatPhone(get(currentChat, "contact.phone", ""))}
					</Text>
				</div>
			</div>
			<div className={styles.actionButtons}>
				<Icon className={styles.searchIcon} name="search" onClick={handleSearch} />
				{permissions.allowTransferChat && !isHistoric && !isMobile && (
					<Button
						disabled={isDisabled && !permissions.allowRemoveChat}
						onClick={() => {
							setOpenTransfer(true);
						}}
						className={styles.buttonNav}>
						<Icon className={styles.buttonIcon} name="exchange" />
						Transferir
					</Button>
				)}
				{permissions.allowFreeChat && !isHistoric && !isMobile && (
					<Button
						disabled={isDisabled && !permissions.allowRemoveChat}
						className={styles.buttonNav}
						onClick={() => {
							setConfirmText("FreeChat");
							setOpenConfirm(true);
						}}>
						<Icon className={styles.buttonIcon} name="sync" />
						Liberar
					</Button>
				)}
				{permissions.allowEndChat && !isHistoric && !isMobile && (
					<Button
						disabled={isDisabled}
						className={`${styles.buttonNav} ${styles.navyColor}`}
						onClick={() => {
							setConfirmText("CloseChat");
							setOpenConfirm(true);
						}}>
						<Icon className={styles.buttonIcon} name="close" />
						Finalizar
					</Button>
				)}
				<Popup
					className={styles.menu}
					on="click"
					position="bottom right"
					open={popupOpen}
					onClose={closePopup}
					onOpen={openPopup}
					pinned
					basic
					trigger={
						<Button className={styles.buttonNav}>
							<Icon name="list" />
						</Button>
					}>
					<Popup.Content>
						<Menu vertical className={styles.menuVertical}>
							{!isHistoric && (
								<Menu.Item
									className={styles.menuItem}
									onClick={() => {
										setOpenQuickAnswers(true);
									}}
									disabled={isDisabled || isWhatsappBlock}>
									<Icon className={styles.menuIcon} name="bolt" />
									<Text className={styles.menuText} disabled={isDisabled || isWhatsappBlock}>
										Respuesta Rápida (/ + R)
									</Text>
								</Menu.Item>
							)}
							{!isHistoric && (
								<Menu.Item
									className={`${styles.menuItem} ${styles.tagMenuItemWrapper}`}
									onClick={() => {
										setOpenTags(true);
									}}
									disabled={isDisabled}>
									<Icon className={styles.menuIcon} name="tag" />
									<Text className={styles.menuText} disabled={isDisabled}>
										Etiquetar (/ + E)
									</Text>
									{!isEmpty(get(currentChat, "tags", [])) && (
										<>
											<Icon className={styles.tagArrowIcon} name="caret right" />
											<div className={styles.tagSubmenu}>
												<div className={styles.tagSubmenuHeader}>Etiquetas asignadas con nota</div>
												{get(currentChat, "tags", []).map((tag, idx) => {
													const nota = notasMap[tag.id] || "";
													return (
														<div
															key={idx}
															className={styles.tagSubmenuItem}
															role="button"
															tabIndex={0}
															onClick={(e) => {
																e.stopPropagation();
																handleTagClickNota(tag);
															}}
															onKeyDown={(e) => {
																if (e.key === "Enter") {
																	e.stopPropagation();
																	handleTagClickNota(tag);
																}
															}}>
															<span className={styles.tagDot} style={{ backgroundColor: tag.color }} />
															<Text className={styles.tagSubmenuText}>{tag.name}</Text>
															{nota.length > 2 && (
																<Popup
																	trigger={
																		<Icon
																			name="comment alternate outline"
																			size="small"
																			className={styles.tagCloudIcon}
																			style={{ color: "#286b92" }}
																		/>
																	}
																	content={nota}
																	position="top center"
																	size="small"
																	inverted
																/>
															)}
														</div>
													);
												})}
											</div>
										</>
									)}
								</Menu.Item>
							)}
							{!isHistoric && (
								<Menu.Item
									className={styles.menuItem}
									onClick={() => {
										setOpenNotePreview(true);
										closePopup();
									}}
									disabled={isDisabled}>
									<Icon className={styles.menuIcon} name="sticky note outline" />
									<Text className={styles.menuText} disabled={isDisabled}>
										Guardar Nota (/ + N)
									</Text>
								</Menu.Item>
							)}
							{!isHistoric && isWhatsappBlock && (
								<Menu.Item
									className={styles.menuItem}
									disabled={isDisabled || noSendTemplate}
									onClick={() => {
										if (!isDisabled && !noSendTemplate) {
											setOpenPlantillaModal(true);
										}
									}}>
									<Icon className={styles.menuIcon} name="chat" />
									<Text className={styles.menuText} disabled={isDisabled || noSendTemplate}>
										Plantilla (/ + P)
									</Text>
								</Menu.Item>
							)}
							{!isHistoric && <Divider className={styles.divider} />}
							{permissions.allowTransferChat && !isHistoric && (
								<Menu.Item
									className={styles.menuItem}
									onClick={() => {
										setOpenTransfer(true);
									}}
									disabled={isDisabled && !permissions.allowRemoveChat}>
									<Icon className={styles.menuIcon} name="exchange" />
									<Text className={styles.menuText} disabled={isDisabled && !permissions.allowRemoveChat}>
										Transferir (/ + T)
									</Text>
								</Menu.Item>
							)}
							{permissions.allowSendFlow && !isHistoric && (
								<Menu.Item
									className={styles.menuItem}
									onClick={() => {
										setEnviarFlow(true);
									}}
									disabled={isDisabled}>
									<Icon className={styles.menuIcon} name="send" />
									<Text className={styles.menuText} disabled={isDisabled}>
										Enviar Flow
									</Text>
								</Menu.Item>
							)}
							{permissions.allowFreeChat && !isHistoric && (
								<Menu.Item
									className={styles.menuItem}
									onClick={() => {
										setConfirmText("FreeChat");
										setOpenConfirm(true);
									}}
									disabled={isDisabled && !permissions.allowRemoveChat}>
									<Icon className={styles.menuIcon} name="sync" />
									<Text className={styles.menuText} disabled={isDisabled && !permissions.allowRemoveChat}>
										Liberar (/ + L)
									</Text>
								</Menu.Item>
							)}
							{permissions.allowEndChat && !isHistoric && (
								<Menu.Item
									className={styles.menuItem}
									onClick={() => {
										setConfirmText("CloseChat");
										setOpenConfirm(true);
									}}
									disabled={isDisabled}>
									<Icon className={styles.menuIcon} name="close" />
									<Text className={styles.menuText} disabled={isDisabled}>
										Finalizar (/ + F)
									</Text>
								</Menu.Item>
							)}
							{!isHistoric && <Divider className={styles.divider} />}
							<Menu.Item className={styles.menuItem}>
								<Icon className={styles.menuIcon}>
									<Checkbox checked={isViewAutogestion} onChange={handleViewAutogestion} />
								</Icon>
								<Text className={styles.menuTextCheck}>Ver Autogestión</Text>
							</Menu.Item>
							<Divider className={styles.divider} />
							{/* <Menu.Item
								className={styles.menuItem}
								onClick={() => {
									setOpenSchedule(true);
								}}
								disabled={isDisabled}>
								<Icon className={styles.menuIcon} name="book" />
								<Text className={styles.menuText} disabled={isDisabled}>
									Agendar
								</Text>
							</Menu.Item> */}
							<Menu.Item className={styles.menuItem} onClick={handlePrint}>
								<Icon className={styles.menuIcon} name="print" />
								<Text className={styles.menuText}>Imprimir</Text>
							</Menu.Item>
						</Menu>
					</Popup.Content>
				</Popup>
			</div>
		</div>
	);
};

ChatHeader.propTypes = {
	dispatch: PropTypes.func,
	isDisabled: PropTypes.bool,
	isHistoric: PropTypes.bool,
	currentChat: PropTypes.object,
	permissions: PropTypes.object,
	handlePrint: PropTypes.func,
	settings: PropTypes.object,
	command: PropTypes.string,
	setCommand: PropTypes.func,
	cmdSearch: PropTypes.string,
	setCmdSearch: PropTypes.func,
	isViewAutogestion: PropTypes.bool,
	isWhatsappBlock: PropTypes.bool,
	onSuccessSend: PropTypes.func,
	userName: PropTypes.string,
	blackListKeyWords: PropTypes.array,
};

function mapStateToProps({ user, company, chats }) {
	return {
		permissions: user.permissions,
		settings: company.settings,
		isViewAutogestion: chats.isViewAutogestion,
		userName: user.name,
		blackListKeyWords: get(company, "settings.blackListKeyWords", []),
	};
}

export default connect(mapStateToProps)(ChatHeader);
